/**
 * Minimal custom progress bar implementation
 * Replaces nprogress dependency with a lightweight alternative
 */

type ProgressConfig = {
    showSpinner?: boolean;
    minimum?: number;
    easing?: string;
    speed?: number;
    trickle?: boolean;
    trickleSpeed?: number;
};

class Progress {
    private status: number | null = null;
    private config: ProgressConfig = {
        showSpinner: false,
        minimum: 0.3,
        easing: 'ease',
        speed: 500,
        trickle: true,
        trickleSpeed: 200,
    };
    private trickleTimer: NodeJS.Timeout | null = null;

    configure(options: ProgressConfig): void {
        Object.assign(this.config, options);
    }

    start(): void {
        if (this.status === null) {
            this.set(this.config.minimum || 0.3);
        }

        if (this.config.trickle) {
            this.trickle();
        }
    }

    set(n: number): void {
        const started = this.status !== null;

        // Clamp between minimum and 1
        n = Math.max(this.config.minimum || 0.3, Math.min(1, n));
        this.status = n;

        const bar = this.getBar();
        if (!bar) {
            if (typeof document !== 'undefined') {
                this.render();
            }
            return;
        }

        const peg = bar.querySelector<HTMLElement>('.progress-peg');

        // Update bar width
        bar.style.transform = `translate3d(${(n - 1) * 100}%, 0, 0)`;
        bar.style.transition = started ? `all ${this.config.speed}ms ${this.config.easing}` : 'none';

        // Update peg visibility
        if (peg) {
            peg.style.opacity = n === 1 ? '0' : '1';
        }
    }

    inc(amount?: number): void {
        let n = this.status || 0;

        if (n >= 1) {
            return;
        }

        if (amount) {
            n += amount;
        } else {
            // Increment by a random amount, slowing as we approach 1
            if (n < 0.2) {
                n += 0.1;
            } else if (n < 0.5) {
                n += 0.04;
            } else if (n < 0.8) {
                n += 0.02;
            } else if (n < 0.99) {
                n += 0.005;
            }
        }

        this.set(n);
    }

    done(): void {
        if (this.status === null) {
            return;
        }

        this.stopTrickle();
        this.set(1);

        // Remove after transition
        setTimeout(() => {
            this.remove();
            this.status = null;
        }, this.config.speed || 500);
    }

    private trickle(): void {
        this.stopTrickle();

        this.trickleTimer = setTimeout(() => {
            this.inc();
            if (this.status !== null && this.status < 1) {
                this.trickle();
            }
        }, this.config.trickleSpeed || 200);
    }

    private stopTrickle(): void {
        if (this.trickleTimer) {
            clearTimeout(this.trickleTimer);
            this.trickleTimer = null;
        }
    }

    private getBar(): HTMLElement | null {
        if (typeof document === 'undefined') {
            return null;
        }
        return document.querySelector<HTMLElement>('#progress-bar .progress-bar');
    }

    private render(): void {
        if (typeof document === 'undefined') {
            return;
        }

        // Check if already rendered
        if (document.getElementById('progress-bar')) {
            return;
        }

        const container = document.createElement('div');
        container.id = 'progress-bar';
        container.innerHTML = `
            <div class="progress-bar">
                <div class="progress-peg"></div>
            </div>
        `;

        document.body.appendChild(container);
    }

    private remove(): void {
        if (typeof document === 'undefined') {
            return;
        }

        const element = document.getElementById('progress-bar');
        if (element) {
            element.remove();
        }
    }
}

// Export singleton instance
export const progress = new Progress();
